import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

const statuses: { name: string; status: boolean }[] = [];
const areAllDone = () => statuses.reduce((a, v) => a && v.status, true);

const onComponentsLoaded = {
  value: false,
  subs: new Array<{ (): void }>(),
  subscribe: (fn: { (): void }) => {
    onComponentsLoaded.subs.push(fn);
    if (onComponentsLoaded.value) fn();
  },
  unsubscribe: (fn: { (available: boolean): void }) => {
    onComponentsLoaded.subs = onComponentsLoaded.subs.filter((sub) => sub !== fn);
  },
  trigger: () => {
    if (onComponentsLoaded.value) return;

    onComponentsLoaded.value = true;
    onComponentsLoaded.subs.forEach((sub) => {
      try {
        sub();
      } catch (e) {
        console.error(e);
      }
    });
  },
};

export function resetComponentsLoaded() {
  onComponentsLoaded.value = false;
}

export function useComponentLoaded(name?: string) {
  const index = useMemo(() => {
    const i = statuses.length;
    statuses.push({
      name: `#${i}${name && ` ${name}`}`,
      status: false,
    });
    return i;
  }, []);

  const tryComplete = useCallback(() => {
    if (statuses[index].status) return;

    statuses[index].status = true;

    if (areAllDone()) {
      onComponentsLoaded.trigger();
    }
  }, []);

  useEffect(() => () => tryComplete(), []);
  return useCallback(() => tryComplete(), []);
}

export function useComponentsLoaded() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const listener = () => setLoaded(true);

    onComponentsLoaded.subscribe(listener);
    return () => onComponentsLoaded.unsubscribe(listener);
  }, []);

  return loaded;
}
